import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { historyKeys } from "../historyKeys";
import { REVERT_MUTATION_KEY } from "../../constants";
import type {
  RevertHistoryEntryArgs,
  RevertHistoryEntryResult,
} from "../../types";
import type {
  UseRevertHistoryEntryArgs,
  UseRevertHistoryEntryResult,
} from "./types";

export const useRevertHistoryEntry = ({
  onReverted,
}: UseRevertHistoryEntryArgs): UseRevertHistoryEntryResult => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: REVERT_MUTATION_KEY,
    mutationFn: ({
      entryId,
    }: RevertHistoryEntryArgs): Promise<RevertHistoryEntryResult> =>
      api.revertQuantitySurveyorHistoryEntry(entryId),
    onSuccess: (result, { direction, planCount }) => {
      if (result.ok) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_HISTORY_REVERTED, {
          direction,
          planCount,
        });
        toast.success(
          t(
            direction === "undo"
              ? "smartSurveyor.history.undoSuccess"
              : "smartSurveyor.history.redoSuccess",
          ),
        );
        onReverted(result);
      } else {
        toast.error(t("smartSurveyor.history.revertRefused"));
      }
    },
    onError: () => {
      toast.error(t("smartSurveyor.history.revertFailed"));
    },
    // Every revert changes the revertibility of older entries, so the whole list is stale.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: historyKeys.all }),
  });

  const { mutate, isPending, variables } = mutation;

  const revert = useCallback(
    (args: RevertHistoryEntryArgs) => {
      // Read from the cache, not from render state, so a second press before the next render is refused too.
      if (queryClient.isMutating({ mutationKey: REVERT_MUTATION_KEY }) > 0) {
        return;
      }
      mutate(args);
    },
    [mutate, queryClient],
  );

  const revertingEntryId = isPending ? variables.entryId : null;

  return { revert, revertingEntryId };
};
